import { isFractionString, normalizeUnicodeFractions } from '@utils/fraction';
import { z } from 'zod';

export const getFirstError = <T extends object>(errorObj: T) => {
	const keys = Object.keys(errorObj);
	if (keys.indexOf('message') != -1) {
		return errorObj['message'];
	}
	let idx = 0;
	while (idx < keys.length) {
		const key: string = keys[idx] as string;
		const result = getFirstError(errorObj[key]);
		if (result) {
			return result;
		}
		idx++;
	}
	return undefined;
};

export function getPrefixedName(prefix: string | undefined, name: string) {
	return prefix?.length ? `${prefix}.${name}` : name;
}

export function getPrefixedNames<TNames extends [...string[]]>(prefix: string | undefined, names: TNames): TNames {
	return names.map((name) => getPrefixedName(prefix, name)) as TNames;
}

/**
 * Zod definition for an optional number coming from
 * a valueAsNumber field.
 *
 * Coerces to a number or undefined.
 */
export const optionalNumber = z
	.number({
		error: (issue) => (issue.input === undefined ? undefined : 'Not a valid number'),
	})
	.or(z.nan())
	.transform((val) => {
		if (val == 0) return val;
		return val ? +val : undefined;
	})
	.optional();

/// Must only be used for hidden input fields using valueAsNumber
export const hiddenInputNumber = z
	.number({
		error: (issue) => (issue.input === undefined ? undefined : 'Not a valid number'),
	})
	.or(z.nan())
	.transform((val) => {
		if (val == 0) return val;
		return val ? +val : undefined;
	})
	.optional();

/**
 * Zod definition for a dimension entered as a fraction (e.g. "24 5/8").
 *
 * Rewrites unicode fractions to their ASCII spelling before validating, so pasted input like
 * "24 ⅝" is accepted and stored as "24 5/8" rather than silently parsing as 24. Decimals are
 * rejected — these fields take fractions. An empty string is allowed for untouched inputs.
 */
export const fractionString = z
	.string()
	.transform(normalizeUnicodeFractions)
	.refine((entry) => entry === '' || isFractionString({ entry }), 'Enter a fraction');

export const nullishFractionString = fractionString.nullish();

export const optionalFractionString = fractionString.optional();

export const optionalNullableFractionString = fractionString.optional().nullable();

export const nullableNumber = z
	.number({
		error: (issue) => (issue.input === undefined ? undefined : 'Not a valid number'),
	})
	.or(z.nan())
	.nullable()
	.transform((val) => {
		if (val == 0) return val;
		return val ? +val : null;
	});
